/**
 * Editor command bus — lets Ask Lilia (and any other surface) launch
 * doc-level editor actions that are then *visible in the editor*: validate
 * the document, open the PDF preview, export/download, find-in-doc.
 *
 * The mechanism is a single window CustomEvent (`lilia:editor-command`).
 * Whichever editor page is mounted (StudioPage for Block, EditorPage for
 * Flow) listens and maps the command onto its own existing handlers, so the
 * action happens where the user can see it. Ask Lilia doesn't need to know
 * which mode is active — it just names the command.
 */

export type EditorCommand = "validate" | "preview" | "export" | "find";

export const EDITOR_COMMAND_EVENT = "lilia:editor-command";

export interface EditorCommandDetail {
  command: EditorCommand;
}

interface CommandMeta {
  /** Button label shown in Ask Lilia. */
  label: string;
  /** What the user sees happen, for the inline confirmation line. */
  launched: string;
  /** Keyword test against a message to decide whether to surface this chip. */
  match: RegExp;
}

export const EDITOR_COMMANDS: Record<EditorCommand, CommandMeta> = {
  validate: {
    label: "Validate document",
    launched: "Opened validation",
    match: /\b(validate|validation|check (for )?errors?|compile errors?|lint|is (this|it|my).*(valid|correct))\b/i,
  },
  preview: {
    // Opens the Preview surface (PDF first; LaTeX · LML tabs available).
    label: "Show preview",
    launched: "Opened PDF preview",
    match: /\b(pdf|preview|compile|render|build the doc|see (the )?document|view (the )?document|download.*pdf|view source|source pane)\b/i,
  },
  export: {
    label: "Export / download",
    launched: "Opened export",
    match: /\b(export|latex source|\.tex\b|download.*(latex|source|zip|file)|arxiv|submission|send to export|share.*link)\b/i,
  },
  find: {
    label: "Find in document",
    launched: "Opened find",
    match: /\b(find|search)\b.*\b(doc|document|text|word|phrase)\b/i,
  },
};

/**
 * Detect which editor commands a free-text message implies. Used by Ask Lilia
 * to surface contextual action chips ("Validate document", "Open PDF
 * preview", …) under a reply. Order is stable + de-duplicated.
 */
export function detectEditorCommands(...texts: string[]): EditorCommand[] {
  const haystack = texts.filter(Boolean).join("\n");
  if (!haystack.trim()) return [];
  const out: EditorCommand[] = [];
  (Object.keys(EDITOR_COMMANDS) as EditorCommand[]).forEach((cmd) => {
    if (EDITOR_COMMANDS[cmd].match.test(haystack)) out.push(cmd);
  });
  return out;
}

/** Fire a command onto the mounted editor page. No-op outside the browser. */
export function runEditorCommand(command: EditorCommand): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<EditorCommandDetail>(EDITOR_COMMAND_EVENT, { detail: { command } }),
  );
}
